(() => {
  class StickyStoryStack extends HTMLElement {
    connectedCallback() {
      this.cleanup();

      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.mobileChapters = Array.from(
        this.querySelectorAll('.sticky-story-stack__mobile-chapter')
      );
      this.videos = Array.from(
        this.querySelectorAll('[data-story-video]')
      );

      this.onResize = () => this.updateMobilePanels();
      window.addEventListener('resize', this.onResize, { passive: true });

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onResize);
      } else {
        this.mobileQuery.addListener(this.onResize);
      }

      if ('ResizeObserver' in window && this.mobileChapters.length) {
        this.panelResizeObserver = new ResizeObserver(() => {
          this.requestPanelUpdate();
        });

        this.mobileChapters.forEach((chapter) => {
          this.panelResizeObserver.observe(chapter);
        });
      }

      this.images = Array.from(this.querySelectorAll('img'));
      this.onImageLoad = () => this.requestPanelUpdate();
      this.images.forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', this.onImageLoad, { once: true });
        }
      });

      if ('IntersectionObserver' in window && this.videos.length) {
        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;

              if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
                const promise = video.play();
                if (promise?.catch) promise.catch(() => {});
              } else {
                video.pause();
              }
            });
          },
          {
            threshold: [0, 0.55, 1]
          }
        );

        this.videos.forEach((video) => {
          this.videoObserver.observe(video);
        });
      }

      this.requestPanelUpdate();
    }

    requestPanelUpdate() {
      cancelAnimationFrame(this.panelUpdateFrame);
      this.panelUpdateFrame = requestAnimationFrame(() => {
        this.updateMobilePanels();
      });
    }

    updateMobilePanels() {
      if (!this.mobileQuery.matches || !this.mobileChapters.length) {
        this.mobileChapters.forEach((chapter) => {
          chapter.style.removeProperty('--story-mobile-panel-top');
        });
        return;
      }

      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      const rootStyles = getComputedStyle(this);
      const configuredTop =
        parseFloat(
          rootStyles.getPropertyValue('--story-mobile-sticky-top')
        ) || 0;

      this.mobileChapters.forEach((chapter) => {
        const panelHeight = chapter.getBoundingClientRect().height;

        /*
          When the complete panel fits in the viewport, pin at configuredTop.
          When it is taller than the viewport, allow it to scroll upward until
          its bottom reaches the viewport bottom, then pin the whole chapter.
        */
        const panelTop = Math.min(
          configuredTop,
          viewportHeight - panelHeight
        );

        chapter.style.setProperty(
          '--story-mobile-panel-top',
          `${Math.round(panelTop)}px`
        );
      });
    }

    cleanup() {
      window.removeEventListener('resize', this.onResize);

      if (this.mobileQuery && this.onResize) {
        if (this.mobileQuery.removeEventListener) {
          this.mobileQuery.removeEventListener('change', this.onResize);
        } else {
          this.mobileQuery.removeListener(this.onResize);
        }
      }

      this.panelResizeObserver?.disconnect();
      this.videoObserver?.disconnect();

      this.images?.forEach((image) => {
        image.removeEventListener('load', this.onImageLoad);
      });

      this.videos?.forEach((video) => video.pause());
      cancelAnimationFrame(this.panelUpdateFrame);
    }

    disconnectedCallback() {
      this.cleanup();
    }
  }

  if (!customElements.get('sticky-story-stack')) {
    customElements.define(
      'sticky-story-stack',
      StickyStoryStack
    );
  }
})();
